<script lang="ts" setup>
import { HeaderWrapper } from '@haskoning/vue-component-library'
import { storeToRefs } from 'pinia'
import { useProjectStore } from '@/stores/project'
import { computed, inject } from 'vue'
import { useProjectUserStore } from '@/stores/projectUser'
import { useRoute, useRouter } from 'vue-router'
import { useCalculationStore } from '@/stores/calculation'
import { useMaterialStore } from '@/stores/material'
import { useCustomRockGradingStore } from '@/stores/customRockGrading'
import { useCostStore } from '@/stores/cost'
import { useShipStore } from '@/stores/ship'
import { useDrawerStore } from '@/stores/drawer'

const authStore = inject('useAuthStore') as any
const router = useRouter()
const route = useRoute()
const drawerStore = useDrawerStore()

const { user } = storeToRefs(authStore)
const {
  writeLoading: projectWriteLoading,
  deleteLoading: projectDeleteLoading,
  currentItem: currentProjectItem
} = storeToRefs(useProjectStore())
const { writeLoading: projectUserWriteLoading, deleteLoading: projectUserDeleteLoading } =
  storeToRefs(useProjectUserStore())
const { writeLoading: calculationWriteLoading, deleteLoading: calculationDeleteLoading } =
  storeToRefs(useCalculationStore())
const { writeLoading: materialWriteLoading, deleteLoading: materialDeleteLoading } =
  storeToRefs(useMaterialStore())
const {
  writeLoading: customRockGradingWriteLoading,
  deleteLoading: customRockGradingDeleteLoading
} = storeToRefs(useCustomRockGradingStore())
const { writeLoading: costWriteLoading, deleteLoading: costDeleteLoading } =
  storeToRefs(useCostStore())

const { writeLoading: shipWriteLoading, deleteLoading: shipDeleteLoading } =
  storeToRefs(useShipStore())

const loading = computed(() => {
  return (
    projectWriteLoading.value ||
    projectDeleteLoading.value ||
    projectUserWriteLoading.value ||
    projectUserDeleteLoading.value ||
    calculationWriteLoading.value ||
    calculationDeleteLoading.value ||
    materialWriteLoading.value ||
    materialDeleteLoading.value ||
    customRockGradingWriteLoading.value ||
    customRockGradingDeleteLoading.value ||
    costWriteLoading.value ||
    costDeleteLoading.value ||
    shipWriteLoading.value ||
    shipDeleteLoading.value
  )
})

const currentRouteName = computed(() => route.name)

const isToggleButtonShown = computed(() => {
  const routeName = currentRouteName.value
  return (
    typeof routeName === 'string' &&
    [
      'calculation-index',
      'calculation-form',
      'materials',
      'costs',
      'vessel',
      'user-management'
    ].includes(routeName)
  )
})

async function logout() {
  await authStore.logout()
  await authStore.$patch({
    user: null
  })
  await router.push({ name: 'auth-login' })
}

const goToProjectIndex = () => {
  router.push({ name: 'project-index' })
}
</script>

<template>
  <HeaderWrapper
    v-if="user"
    title="ARMOUR"
    image-src="/logo-rhdhv-white.png"
    :user="user"
    :loading="loading"
    @logo-click="goToProjectIndex"
    @logout="logout()"
  >
    <template v-if="isToggleButtonShown" v-slot:toolbar-prepend>
      <v-app-bar-nav-icon
        class="pl-4 mx-1 no-overlay"
        height="36"
        width="36"
        color="white"
        :ripple="false"
        :icon="drawerStore.drawer.shown ? 'mdi-close' : 'mdi-menu'"
        @click="drawerStore.toggleDrawer"
      />
    </template>

    <template v-slot:breadcrumbs-items>
      <v-breadcrumbs-item>
        <router-link
          class="text-white toolbar-text text-decoration-none"
          :to="{ name: 'project-index' }"
        >
          Projects
        </router-link>
      </v-breadcrumbs-item>

      <template v-if="currentProjectItem?.id && $route.name !== 'project-index'">
        <v-breadcrumbs-divider />
        <v-breadcrumbs-item>
          <router-link
            class="text-white toolbar-text text-decoration-none"
            :to="{ name: 'project-detail', params: { id: $route.params.id } }"
          >
            {{ currentProjectItem?.name }}
          </router-link>
        </v-breadcrumbs-item>
      </template>
    </template>
  </HeaderWrapper>
</template>

<style scoped>
.no-overlay :deep(.v-btn__overlay) {
  display: none;
}
</style>
